
import React from 'react';
import { motion } from 'framer-motion';
import { Check, Users, TrendingUp, Rocket, Building, Heart, Brain } from 'lucide-react';
import LuxuryButton from "@/components/ui/luxury-button";

const PartnerTargetAudience = () => {
  const practiceTypes = [
    {
      icon: Users,
      title: "PPO-Weary Dentists",
      description: "Tired of write-offs and burnout? JUV gives you a fee-for-service escape hatch — with a new stream of high-margin cash revenue delivered by our Nurse Practitioners."
    },
    {
      icon: TrendingUp,
      title: "Cosmetic & Implant Specialists",
      description: "Want to elevate your brand and increase case acceptance? JUV turns every smile makeover into a full-face transformation with injectables, facials, and whitening."
    },
    {
      icon: Rocket,
      title: "Startup Offices",
      description: "Still ramping up? We bring patients, branding, and recurring revenue — fast. No equipment. No overhead. Just results."
    },
    {
      icon: Building,
      title: "Multi-Location Groups",
      description: "Looking to scale profit across locations? JUV activates unused ops and empty chairs into $30K+/month medspa engines. One contract. Endless upside."
    },
    {
      icon: Heart,
      title: "Hygiene-Heavy Practices",
      description: "Turn your cleaning patients into wellness members with Botox, whitening, and glow therapy — all delivered during routine hygiene visits."
    },
    {
      icon: Brain,
      title: "TMJ, Sleep & Airway Dentists",
      description: "Already offering holistic care? JUV layers on peptides, NAD+, facial esthetics and HRT to complete the mind-body-patient experience."
    }
  ];

  return (
    <section className="py-32 bg-white">
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="text-4xl mb-4">🦷</div>
          <h2 className="text-5xl md:text-6xl font-serif font-light text-juv-deep-chestnut mb-6">
            Who Is JUV For?
          </h2>
          <p className="text-2xl text-juv-deep-chestnut/80 leading-relaxed max-w-4xl mx-auto">
            Whether you're running a growing startup or a high-end boutique office, JUV Medspa™ is designed to add <span className="font-semibold text-juv-terracotta">$30,000/month in new revenue</span> — without adding more dental work.
          </p>
          <p className="text-xl text-juv-deep-chestnut/70 mt-4">
            We partner with:
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {practiceTypes.map((practice, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-juv-cream-white rounded-3xl p-8 shadow-luxury hover:shadow-editorial transition-all duration-300 hover:scale-105"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 bg-juv-terracotta rounded-full flex items-center justify-center mb-2">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div className="w-12 h-12 bg-juv-terracotta/10 rounded-2xl flex items-center justify-center">
                    <practice.icon className="w-6 h-6 text-juv-terracotta" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-juv-deep-chestnut mb-3">
                    {practice.title}
                  </h3>
                  
                  <p className="text-juv-deep-chestnut/70 leading-relaxed">
                    {practice.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Path to Fee-for-Service Freedom Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-juv-deep-chestnut rounded-3xl p-12 text-center text-white"
        >
          <div className="text-3xl mb-4">🎯</div>
          <h3 className="text-4xl font-serif font-light mb-6">
            Your Path to Fee-for-Service Freedom
          </h3>
          
          <div className="text-xl mb-8 space-y-2">
            <p>No tools. No training. No staff headaches.</p>
            <p>Just a fully managed, tech-powered wellness engine under your roof.</p>
          </div>
          
          <p className="text-lg mb-8 text-white/90">
            Let JUV be your stepping stone to a modern, cash-based, high-profit future.
          </p>
          
          <LuxuryButton 
            size="lg" 
            variant="secondary"
            className="px-12 py-6 text-xl rounded-full font-medium bg-white text-juv-deep-chestnut hover:bg-white/95"
          >
            👉 Join the JUV Network
          </LuxuryButton>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnerTargetAudience;
